export const SUBSCRIPTION_TIERS = [
  "free",
  "starter",
  "pro",
  "enterprise",
] as const;
export type SubscriptionTier = (typeof SUBSCRIPTION_TIERS)[number];

export const SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "unpaid",
  "paused",
  "none",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const ACTIVE_STATUSES: readonly SubscriptionStatus[] = [
  "active",
  "trialing",
  "past_due",
] as const;

export function isActiveStatus(
  status: SubscriptionStatus | undefined,
): boolean {
  if (!status) return false;
  return ACTIVE_STATUSES.includes(status);
}

export interface TierLimits {
  listings: number;
  productTypes: number;
  locations: number;
  teamMembers: number;
}

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    listings: Number.MAX_SAFE_INTEGER,
    productTypes: Number.MAX_SAFE_INTEGER,
    locations: Number.MAX_SAFE_INTEGER,
    teamMembers: 0,
  },
  starter: {
    listings: Number.MAX_SAFE_INTEGER,
    productTypes: Number.MAX_SAFE_INTEGER,
    locations: Number.MAX_SAFE_INTEGER,
    teamMembers: 1,
  },
  pro: {
    listings: Number.MAX_SAFE_INTEGER,
    productTypes: Number.MAX_SAFE_INTEGER,
    locations: Number.MAX_SAFE_INTEGER,
    teamMembers: Number.MAX_SAFE_INTEGER,
  },
  enterprise: {
    listings: Number.MAX_SAFE_INTEGER,
    productTypes: Number.MAX_SAFE_INTEGER,
    locations: Number.MAX_SAFE_INTEGER,
    teamMembers: Number.MAX_SAFE_INTEGER,
  },
};

export function getTierLimits(tier: SubscriptionTier): TierLimits {
  return TIER_LIMITS[tier];
}

export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
};

export interface SubscriptionInfo {
  tier?: SubscriptionTier;
  status?: SubscriptionStatus;
}

const FREE_TIER_STATUS_SET = new Set<SubscriptionStatus>([
  "canceled",
  "none",
  "incomplete",
  "incomplete_expired",
  "unpaid",
  "paused",
]);

export function getEffectiveTierFromSubscription(
  subscription: SubscriptionInfo | undefined | null,
): SubscriptionTier {
  if (!subscription) return "free";
  if (!subscription.status || FREE_TIER_STATUS_SET.has(subscription.status)) {
    return "free";
  }
  return subscription.tier ?? "free";
}

export function hasPaidTier(tier: SubscriptionTier): boolean {
  return tier === "starter" || tier === "pro" || tier === "enterprise";
}
